import React from 'react';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import Link from 'next/link';
import { headingColor } from 'services/styleUtils/headingStyleUtils';

const Container = styled('div')(({ $backgroundColor }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignContent: 'center',
  width: '100%',
  cursor: 'pointer',
  '@media screen and (max-width: 1199px)': {
    backgroundColor: 'rgba(25, 118, 210, 1)',
    position: 'relative',
    height: '240px',
    margin: '20px 0 30px 0',
  },
  '@media screen and (max-width: 899px)': {
    backgroundColor: 'rgba(25, 118, 210, 1)',
    position: 'relative',
    height: '160px',
    margin: '20px 0 30px 0',
  },
  '@media screen and (min-width: 1200px)': {
    backgroundColor: $backgroundColor,
    backgroundImage: 'url(${fields.backgroundWallpaper.url})',
    position: 'relative',
    height: '265px',
    margin: '20px 0 50px 0',
  },
}));

const ImageContainer = styled('div')({
  '@media screen and (max-width: 1199px)': {
    display: 'none',
  },
  '@media screen and (min-width: 1200px)': {
    marginTop: '32.2px',
    height: '235px',
    padding: 0,
  },
});

const OptionImage = styled('div')({
  '@media screen and (max-width: 1199px)': {
    display: 'none',
  },
  '@media screen and (min-width: 1200px)': {
    margin: 0,
    height: '235px',
  },
});

const CenterSpaceHolder = styled('div')({
  '@media screen and (max-width: 1199px)': {
    height: '95%',
    width: '61%',
  },
  '@media screen and (max-width: 899px)': {
    height: '95%',
    width: '70%',
  },
  '@media screen and (min-width: 1200px)': {
    position: 'relative',
    margin: '26px 0 0 -15px',
    backgroundColor: 'transparent',
    height: '254.9px',
    padding: '25px',
    width: '700px',
  },
});

const CenterArea = styled('div')(({ theme, $calloutPanelColor, $calloutTriangleColor }) => ({
  '@media screen and (max-width: 1199px)': {
    height: '95%',
    width: '95%',
    backgroundColor: 'rgba(25, 118, 210, 1)',
  },
  '@media screen and (max-width: 899px)': {
    height: '98%',
    width: '95%',
    backgroundColor: 'rgba(25, 118, 210, 1)',
  },
  '@media screen and (min-width: 1200px)': {
    position: 'absolute',
    left: '2%',
    top: '-10%',
    margin: '26px 0 0 -15px',
    backgroundColor: $calloutPanelColor,
    height: '254.9px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: '25px',
    width: '700px',
    '&:after': {
      borderBottom: 'solid 16px transparent',
      borderRight: 'solid 16px transparent',
      borderLeft: `solid 16px ${$calloutTriangleColor}`,
      transform: 'translateX(-50%)',
      content: '""',
      position: 'absolute',
      top: '93.45%',
      left: '102.25%',
      height: 0,
      width: 0,
    },
  },
}));

const Title = styled('div')(({ $calloutHeaderFontColor }) => ({
  '@media screen and (max-width: 1199px)': {
    width: '400px',
    margin: '10px 20px',
    fontSize: '1.975rem',
    lineHeight: '2.225rem',
    fontWeight: 600,
    color: $calloutHeaderFontColor,
    height: '50%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  '@media screen and (max-width: 899px)': {
    width: '300px',
    margin: '10px 10px 0 10px',
    fontSize: '1.425rem',
    lineHeight: '1.875rem',
    fontWeight: 600,
    color: $calloutHeaderFontColor,
    height: '98%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  '@media screen and (min-width: 1200px)': {
    width: '600px',
    margin: '10px',
    fontSize: '2.625rem',
    lineHeight: '2.875rem',
    fontWeight: 600,
    color: $calloutHeaderFontColor,
  },
}));

const SubTitle = styled('div')(({ $textFontColor }) => ({
  '@media screen and (max-width: 1199px)': {
    width: '90%',
    margin: '10px 20px',
    fontSize: '1.25rem',
    lineHeight: '1.75rem',
    color: $textFontColor,
  },
  '@media screen and (max-width: 899px)': {
    display: 'none',
  },
  '@media screen and (min-width: 1200px)': {
    width: '600px',
    margin: '10px',
    fontSize: '1.25rem',
    lineHeight: '1.75rem',
    color: $textFontColor,
  },
}));

const IconContainer = styled('div')({
  minHeight: '80px',
  '@media screen and (max-width: 1199px)': {
    margin: '90px 0 0 30px',
  },
  '@media screen and (max-width: 899px)': {
    margin: '45px 0 0 30px',
  },
  '@media screen and (min-width: 1200px)': {
    margin: '110px 0 0 30px',
  },
});

const IconImage = styled('div')({
  margin: 0,
  marginBottom: '10px',
  width: '80px',
});

const _BackgroundImg = styled('img')(({ theme: _theme }) => ({
  borderRadius: '6px',
  display: 'block',
  width: '20rem',
  height: 'auto',
}));

const CalloutWithBackground = ({ module }) => {
  const { fields } = module;
  const theme = useTheme();

  return (
    <Link href={fields.iconUrl.href} target={fields.iconUrl.target}>
      <Container $backgroundColor={headingColor(theme, fields.backgroundColor)}>
        <ImageContainer>
          <OptionImage>
            <img
              src={fields.backgroundImage?.url}
              alt={fields.backgroundImage?.label}
            />
          </OptionImage>
        </ImageContainer>
        <CenterSpaceHolder>
          <CenterArea
            $calloutPanelColor={headingColor(theme, fields.calloutPanelColor)}
            $calloutTriangleColor={headingColor(theme, fields.calloutTriangleColor)}
          >
            <Title $calloutHeaderFontColor={fields.calloutHeaderFontColor}>
              {fields.calloutHeading}
            </Title>
            <SubTitle $textFontColor={fields.textFontColor}>
              {fields.calloutText}
            </SubTitle>
          </CenterArea>
        </CenterSpaceHolder>
        <IconContainer>
          <IconImage>
            <img
              src={fields.backgroundIcon?.url}
              alt={fields.backgroundIcon?.label}
            />
          </IconImage>
        </IconContainer>
      </Container>
    </Link>
  );
};
export default CalloutWithBackground;
